import { Settings, Shield, ShieldCheck, MoreHorizontal, ToggleLeft, ToggleRight } from "lucide-react";

import ActionMenu from "../../../components/shared/ActionMenu";
import DataTable from "../../../components/shared/DataTable";
import { Button } from "../../../components/ui/button";
import { Switch } from "../../../components/ui/switch";
import { Label } from "../../../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";

const RoleTable = ({
  roles,
  permissions,
  onEdit,
  onDelete,
  onConfigurePermissions,
  showDeleted,
  setShowDeleted,
  setCurrentPage
}) => {
  const roleColumns = [
    {
      header: "Role Name",
      accessor: "name",
      isPrimary: true,
      render: (val, role) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-100 dark:bg-yale-blue-800 rounded-lg shrink-0">
            <Shield className="w-5 h-5 text-yale-blue-500" />
          </div>
          <div className="flex items-center">
            <span className="text-sm font-semibold text-yale-blue-900 dark:text-slate-100">{val}</span>
            {role.deleted && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 uppercase tracking-wider shrink-0 ml-2">
                Deleted
              </span>
            )}
          </div>
        </div>
      )
    },
    {
      header: "Tenant ID",
      accessor: "tenantId",
      render: (val, tenant) => (
        <span className="text-xs font-mono text-slate-500 bg-slate-100 dark:bg-yale-blue-800 px-2 py-1 rounded">
          {tenant?.name || "system"}
        </span>
      )
    },
    {
      header: "Access Configuration",
      accessor: "permissions",
      render: (_, role) => (
        <div className="flex items-center gap-2">
          {permissions?.canUpdate && !role.deleted && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs bg-white dark:bg-yale-blue-900 border-slate-200 dark:border-yale-blue-800 hover:border-yale-blue-400 dark:hover:border-yale-blue-600 px-2 md:px-3"
              onClick={() => onConfigurePermissions(role)}
            >
              <Settings className="w-3.5 h-3.5 md:mr-2 text-yale-blue-500" />
              <span className="hidden md:inline">Configure Permissions</span>
              <span className="md:hidden">Configure</span>
            </Button>
          )}
        </div>
      )
    },
    {
      header: "Actions",
      filter: permissions?.canDelete ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${showDeleted ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-4 h-4 hidden md:block" />
              <span className="md:hidden">
                {showDeleted ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" align="end">
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={showDeleted}
                onCheckedChange={(val) => {
                  setShowDeleted(val);
                  setCurrentPage(0);
                }}
                id="show-deleted-roles-table"
              />
              <Label htmlFor="show-deleted-roles-table" className="text-xs font-semibold cursor-pointer whitespace-nowrap">
                Show Deleted
              </Label>
            </div>
          </PopoverContent>
        </Popover>
      ) : null,
      accessor: "id",
      render: (_, role) => (
        <ActionMenu
          canEdit={permissions?.canUpdate && !role.deleted}
          canDelete={permissions?.canDelete && !role.deleted}
          onEdit={() => onEdit(role)}
          onDelete={() => onDelete(role.id)}
        />
      )
    }
  ];

  return (
    <DataTable
      columns={roleColumns}
      data={roles}
      emptyStateTitle="No roles found"
      emptyStateDescription="Create custom roles to define granular access controls."
      emptyStateIcon={ShieldCheck}
    />
  );
};

export default RoleTable;
